import { DOCUMENT } from '@angular/common';
import {
  Injectable,
  NgZone,
  Renderer2,
  RendererFactory2,
  inject,
} from '@angular/core';
import {
  AddressValidationInput,
  AddressValidationResult,
  Discrepancy,
  AppConfig,
  GoogleMapsAddressComponentType,
} from '@boels/models';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { WindowRef } from '../window/window-ref';

declare global {
  interface Window {
    initMap: () => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AddressValidationService {
  private zone = inject(NgZone);
  private winRef = inject(WindowRef);
  private environment = inject<AppConfig>('ENVIRONMENT' as any, {
    optional: true,
  });
  private rendererFactory = inject(RendererFactory2);
  private document = inject<Document>(DOCUMENT);

  private geocoder: google.maps.Geocoder;
  apiLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private renderer: Renderer2;

  constructor() {
    if (this.winRef.isBrowser()) {
      this.renderer = this.rendererFactory.createRenderer(null, null);
      this.loadScript();
    }
  }

  getUserAddress(
    requestedComponents: GoogleMapsAddressComponentType[],
    userInputs: AddressValidationInput,
  ): string {
    const userAddressComponents = requestedComponents.map(
      (component) => userInputs.values[component] || '',
    );
    return userAddressComponents.join(', ');
  }

  createCorrectedAddress(
    googleResponse: google.maps.GeocoderAddressComponent[] | undefined,
    userInputs: AddressValidationInput,
  ): Record<string, string> {
    const correctedAddress: Record<string, string> = {};

    for (const [field, componentTypes] of Object.entries(userInputs.fieldMap)) {
      if (!componentTypes) continue;
      const foundComponents = componentTypes.map((type) =>
        googleResponse?.find((item) => item.types.includes(type)),
      );
      const foundComponent = foundComponents.find((comp) => comp !== undefined);

      if (foundComponent) {
        correctedAddress[field] = foundComponent.long_name;
      } else {
        correctedAddress[field] = userInputs.values[componentTypes[0]] || '';
      }
    }

    return correctedAddress;
  }

  identifyDiscrepancies(
    googleResponse: google.maps.GeocoderAddressComponent[] | undefined,
    userInputs: AddressValidationInput,
  ): Discrepancy | undefined {
    const discrepancies: Discrepancy = {};

    for (const [field, componentTypes] of Object.entries(userInputs.fieldMap)) {
      if (!componentTypes) continue;

      let discrepancyFound = false;
      let expectedValue: string | undefined;
      let actualValue: string | undefined;

      for (const type of componentTypes) {
        const component = googleResponse?.find((item) =>
          item.types.includes(type),
        );

        if (component) {
          const userInput = userInputs.values[field]
            ?.toLowerCase()
            .replace(' ', '');
          const longName = component.long_name?.toLowerCase().replace(' ', '');
          const shortName = component.short_name
            ?.toLowerCase()
            .replace(' ', '');

          if (userInput === longName || userInput === shortName) {
            discrepancyFound = false;
            break;
          } else {
            expectedValue = component.long_name;
            actualValue = userInput || '';
            discrepancyFound = true;
          }
        }
      }

      if (discrepancyFound && expectedValue && actualValue) {
        discrepancies[componentTypes[0]] = {
          expected: expectedValue,
          actual: actualValue,
        };
      }
    }

    return Object.keys(discrepancies).length > 0 ? discrepancies : undefined;
  }

  /**
   * Creates a validationResult Object with optional google maps validation.
   * @param language Language in isocode for geocodation api.
   * @param userInputs UserInputs defined by key value pairs.
   * Use multiple GoogleMapsAddressComponentTypes in fieldMap to cover every possible variation of results.
   * example userInput:
   * values: {
          line1: line1Value,
          line2: line2Value,
          town: townValue,
          postalCode:,
        },
        fieldMap: {
          line1: [GoogleMapsAddressComponentType.ROAD],
          line2: [GoogleMapsAddressComponentType.HOUSE_NUMBER],
          town: [
            GoogleMapsAddressComponentType.TOWN_OR_CITY,
            GoogleMapsAddressComponentType.SUB_LOC,
          ],
          postalCode: [GoogleMapsAddressComponentType.POSTAL_CODE],
        },
   * @returns AddressValidationResult. If found = true and discrepancies = undefined the address is valid.
        If google maps Script cannot be loaded the result is { found: true }.
   */
  validateAddress(
    language: string,
    userInputs: AddressValidationInput,
  ): Observable<AddressValidationResult> {
    return this.apiLoaded.pipe(
      switchMap((loaded: boolean) => {
        if (loaded) {
          return new Observable<AddressValidationResult>((observer) => {
            const userAddress = Object.values(userInputs.values).join(', ');

            this.geocoder?.geocode(
              { address: userAddress, language: language },
              (
                results: google.maps.GeocoderResult[] | null,
                status: google.maps.GeocoderStatus,
              ) => {
                this.zone.run(() => {
                  const result: AddressValidationResult = {
                    found: false,
                    userInputs,
                  };

                  if (
                    status === google.maps.GeocoderStatus.OK &&
                    results &&
                    results[0]
                  ) {
                    const googleResponse: google.maps.GeocoderResult =
                      results[0];

                    const componentsInGoogleResponse =
                      googleResponse.address_components
                        .map((component) => component.types)
                        .flat();

                    const allComponentsPresent = Object.entries(
                      userInputs.fieldMap,
                    ).every(([, componentTypes]) =>
                      componentTypes?.some((type) =>
                        componentsInGoogleResponse.includes(type),
                      ),
                    );

                    if (allComponentsPresent) {
                      result.found = true;
                      result.discrepancies = this.identifyDiscrepancies(
                        googleResponse.address_components,
                        userInputs,
                      );
                      result.correctedAddressList = this.createCorrectedAddress(
                        googleResponse.address_components,
                        userInputs,
                      );
                    }
                  }
                  observer.next(result);
                  observer.complete();
                });
              },
            );
          });
        } else {
          return of({ found: true }); // return true as validation is optional
        }
      }),
    );
  }

  loadScript(): Promise<void> {
    if (this.document.getElementById('googleMapsScript')) {
      this.apiLoaded.next(true);
      return Promise.resolve();
    }
    const apiKey = this.environment?.googleMaps.apiKey;

    // Callback function on success
    if (this.winRef && this.winRef.nativeWindow) {
      this.winRef.nativeWindow['initMap'] = () => {
        this.geocoder = new google.maps.Geocoder();
        this.apiLoaded.next(true);
      };
    }

    return new Promise((resolve, reject) => {
      const script = this.renderer.createElement('script');
      script.id = 'googleMapsScript';
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&callback=initMap`;
      script.onload = resolve;
      script.onerror = reject;
      this.renderer.appendChild(this.document.head, script);
    });
  }
}
